/**
 * Identifies if an interest rate config has a low and optimal point.
 *
 * We can't check equality with the bigint values because they are actually
 * slightly different, likely due to smart contract constraints. For example,
 * here is how an IRM may look in this scenario:
 *
 * ```json
 * {
 *   "uopt": "900000000000000001",
 *   "ucrit": "900000000000000002",
 *   "ulow": "900000000000000000",
 *   "ki": "0",
 *   "kcrit": "20991882293",
 *   "klow": "7046620440",
 *   "klin": "0",
 *   "beta": "17483495580172",
 *   "ri": "6341958396",
 *   "Tcrit": "15079759706836472408"
 * }
 * ```
 *
 * The best way to identify this situation is to check if the points are the
 * same when rounded to a whole percent.
 */

import { IInterestRateModel } from '@silo/typechain';
import { asWholePct, isUndefined } from '@silo/utils';

interface IrmMeta {
  hasLow: boolean;
  hasOptimal: boolean;
}

export function getIrmMeta(config: IInterestRateModel.ConfigStructOutput | undefined): IrmMeta {
  if (isUndefined(config)) {
    return {
      hasLow: true,
      hasOptimal: true,
    };
  }

  const formattedOpt = asWholePct(config.uopt);
  const formattedCrit = asWholePct(config.ucrit);
  const formattedLow = asWholePct(config.ulow);

  return {
    hasLow: formattedLow !== formattedOpt,
    hasOptimal: formattedOpt !== formattedCrit,
  };
}
